import React from 'react';
import { Box, Button, CircularProgress, MenuItem, TextField, Typography } from '@mui/material';
import InlineAlert from '../../../shared/components/InlineAlert';
import { ModelUse } from '../../../types/backend';
import type { AvailableModel, ModelUseType } from '../../../types/backend';
import { useModelBindings } from '../hooks/useModelBindings';
import { modelOptions } from './modelOptions';

// UI-only labels for the prompt uses, in the order the use case lists them (PS-UC-17).
const USE_META: Record<ModelUseType, { label: string; purpose: string }> = {
  [ModelUse.EVENT_ENRICHMENT]: { label: 'Event summary', purpose: 'The AI summary written for each firing of a rule' },
  [ModelUse.EVENT_FORMATTING]: { label: 'Ticket and channel text', purpose: 'The ITSM ticket and channel message a rule delivers' },
  [ModelUse.MAPPING_SUGGESTION]: { label: 'Field mapping suggestion', purpose: 'The field mapping suggested to an operator' },
  [ModelUse.QUEST_MASTER]: { label: 'Quest narrative', purpose: 'The story told around a gamification quest' },
};

const USES = Object.values(ModelUse) as ModelUseType[];

const NOT_SET = '';

interface RowProps {
  use: ModelUseType;
  selected: string | null;
  models: AvailableModel[];
  modelsLoading: boolean;
  modelsError: string | null;
  dirty: boolean;
  saving: boolean;
  error: string | null;
  saved: boolean;
  onChoose: (modelId: string) => void;
  onSave: () => void;
}

const BindingRow: React.FC<RowProps> = ({
  use, selected, models, modelsLoading, modelsError, dirty, saving, error, saved, onChoose, onSave,
}) => {
  const { label, purpose } = USE_META[use];
  const storedListing = selected !== null ? models.find((m) => m.model_id === selected) : undefined;
  // Only a catalog that was read can say a model is missing from it or not callable.
  const catalogRead = modelsError === null && !modelsLoading;
  const storedUnlisted = catalogRead && selected !== null && storedListing === undefined;
  const storedNotCallable = catalogRead && storedListing !== undefined && !storedListing.available;

  const helperText = modelsError !== null
    ? undefined
    : modelsLoading ? 'Reading what this environment can run...' : 'Models this environment cannot run are shown disabled';

  return (
    <Box role="group" aria-label={label} sx={{ py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
      <Typography variant="subtitle2">{label}</Typography>
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>{purpose}</Typography>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
        <TextField
          select
          label="Model"
          value={selected ?? NOT_SET}
          onChange={(e) => onChoose(e.target.value)}
          helperText={helperText}
          disabled={modelsError !== null}
          size="small"
          sx={{ minWidth: 280, flex: '1 1 360px' }}
          SelectProps={{
            displayEmpty: true,
            renderValue: (value) => {
              if (value === NOT_SET) {
                return <Typography component="span" variant="body2" color="text.secondary">Not set</Typography>;
              }
              const listing = models.find((m) => m.model_id === value);
              return listing?.label ?? String(value);
            },
          }}
          InputLabelProps={{ shrink: true }}
        >
          {/* The stored model stays an option even when the catalog does not list it, so the row
              never renders blank over a model it still runs on. Unread catalog: no caption. */}
          {selected !== null && storedListing === undefined && (
            <MenuItem value={selected}>
              {selected}
              {storedUnlisted && (
                <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 1 }}>
                  not in this environment
                </Typography>
              )}
            </MenuItem>
          )}
          {modelOptions(models)}
        </TextField>
        <Button variant="contained" size="small" onClick={onSave} disabled={!dirty || saving} sx={{ mt: 0.5 }}>
          {saving ? <CircularProgress size={16} color="inherit" /> : 'Save'}
        </Button>
      </Box>
      {storedNotCallable && storedListing?.reason && (
        <Typography variant="caption" color="warning.main" component="p">{storedListing.reason}</Typography>
      )}
      {modelsError && <InlineAlert severity="error" sx={{ mt: 1 }}>{modelsError}</InlineAlert>}
      {error && <InlineAlert severity="error" sx={{ mt: 1 }}>{error}</InlineAlert>}
      {saved && <InlineAlert severity="success" sx={{ mt: 1 }}>Saved</InlineAlert>}
    </Box>
  );
};

const ModelBindings: React.FC<{ active: boolean }> = ({ active }) => {
  const bindings = useModelBindings({ active });

  return (
    <Box sx={{ px: 3, py: 2, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
      <Typography variant="h6">Pipeline prompt models</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        The model each prompt that runs outside an agent uses. An edit takes effect within five minutes.
      </Typography>
      {bindings.loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
      ) : bindings.loadError ? (
        <InlineAlert severity="error">{bindings.loadError}</InlineAlert>
      ) : (
        USES.map((use) => (
          <BindingRow
            key={use}
            use={use}
            selected={bindings.selected(use)}
            models={bindings.models}
            modelsLoading={bindings.modelsLoading}
            modelsError={bindings.modelsError}
            dirty={bindings.dirty(use)}
            saving={bindings.saving(use)}
            error={bindings.error(use)}
            saved={bindings.savedUse === use}
            onChoose={(modelId) => bindings.choose(use, modelId)}
            onSave={() => bindings.save(use)}
          />
        ))
      )}
    </Box>
  );
};

export default ModelBindings;
